import styled, { css } from 'styled-components'
import {
  H1,
  H2,
  H3,
  H4,
  H5,
  P,
  Strong,
} from '../../shared_components/Library/Typeography'
import { Box } from '../../shared_components/UI/Box'
import { ThemedButton } from '../../shared_components/ThemedButton'
import { PureBackButton } from '../../shared_components/Page'
import { ReactNode, useState } from 'react'
import { Dialog, DialogContent } from '../../shared_components/Digalog'
import { Toaster } from '../../App'
import AlertCircle from '../../shared_components/Icons/AlertCircle'
import CustomCheckbox from '../Account/CustomCheckbox'
import { CheckboxItem } from '../Account/styles'
import { useHistory } from 'react-router-dom'
import { OutcomeAlert } from './OutcomeAlert'
import routes from '../../router/routes'
import { useWindowSize } from '@uidotdev/usehooks'
import { useStakingContext } from '../../contexts/StakingContext'
import { toast } from 'react-toastify'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import ConnectWalletButton from '../../shared_components/Library/Buttons/ConnectWalletButton'
import { ClaimButton } from './Stake/styles'

type TransactionsStatus =
  | 'idle'
  | 'claiming'
  | 'approve-spend'
  | 'approve-stake'
  | 'staking'
  | 'done'

type Status = 'idle' | 'transactions'

function StakingCheckout({
  target,
  type,
  amount,
  months,
  vaultsAvailable,
  tokensAvailable,
  totalApy,
  multiplier,
  estimatedExtraTokens,
  maxEstimatedExtraTokens,
  estimatedTokensForClaim,
  estimatedTokensPerVault,
  isOpen,
  dismiss,
}: {
  target: 'tokens' | 'vaults'
  type: 'stake' | 'claim'
  amount: number
  months: number
  vaultsAvailable: number
  tokensAvailable: number
  totalApy: number
  multiplier: number
  estimatedExtraTokens: number
  maxEstimatedExtraTokens: number
  estimatedTokensForClaim: number
  estimatedTokensPerVault: bigint
  isOpen: boolean
  dismiss: () => void
}) {
  const { isLoggedIn } = useIsLoggedIn()
  const { push } = useHistory()
  const { width: screenWidth } = useWindowSize()
  const isMobile = screenWidth < 1000

  const [hasConfirmedClaimAction, setHasConfirmedClaimAction] = useState(false)

  const name = target === 'tokens' ? 'Tokens' : 'Vaults'
  const total = target === 'tokens' ? tokensAvailable : vaultsAvailable
  const iconSrc = target === 'tokens' ? '/zap-coin.svg' : '/vault-icon.svg'

  const isStake = type === 'stake'
  const isClaim = type === 'claim'
  const [autoRelock, setAutoRelock] = useState(true)

  const rawTotalTokens = Number(estimatedTokensPerVault) * Number(amount)
  const estTokenValueTotal = isClaim
    ? estimatedTokensForClaim
    : estimatedExtraTokens

  // prob can remove in favor of [tokens/vaults]StakingStatus
  const [transactionsStatus, setTransactionsStatus] =
    useState<TransactionsStatus>('idle')
  const [status, setStatus] = useState<Status>('idle')

  const {
    stakeTokens,
    tokensStakingStatus,
    stakeVaults,
    vaultsStakingStatus,
    claimVaults,
    vaultsClaimStatus,
  } = useStakingContext()

  const onCheckout = async () => {
    setStatus('transactions')

    try {
      if (target === 'tokens') {
        await stakeTokens(months, amount, autoRelock)
        push(routes.myzap.tokensStaked)
        setStatus('idle')
        return
      }
    } catch (error) {
      setStatus('idle')
      console.error('Error staking tokens', error)
      toast.error('Something went wrong. Please try again')
    }

    if (target === 'vaults') {
      if (isClaim) {
        try {
          await claimVaults(amount)
          push(routes.myzap.vaultsClaimed)
          setStatus('idle')
        } catch (error) {
          setStatus('idle')
          console.error('Error claiming vaults', error)
          toast.error('Something went wrong. Please try again')
        }
      } else {
        try {
          await stakeVaults(months, amount, autoRelock)
          push(routes.myzap.vaultsStaked)
          setStatus('idle')
        } catch (error) {
          setStatus('idle')
          console.error('Error staking vaults', error)
          toast.error('Something went wrong. Please try again')
        }
      }
    }
  }

  const onClose = () => {
    setHasConfirmedClaimAction(false)
    dismiss()
  }

  const onComplete = () => {
    onClose()
    push('/account')
  }

  if (target === 'vaults' && isClaim && !hasConfirmedClaimAction) {
    return (
      <CheckoutModalOverlay isOpen={isOpen} dismiss={dismiss}>
        <div style={{ width: isMobile ? '340px' : '628px', maxWidth: '100%' }}>
          {status === 'idle' && (
            <Box dc g={32}>
              <PureBackButton onClick={onClose} />
              <Box dc jc ac g="var(--units-32, 32px)">
                <img
                  src="/img/cry-zepeppe.png"
                  style={{ width: '320px', height: '184px' }}
                />
                <H1 style={{ textAlign: 'center' }} s="2XL">
                  THIS IS IRREVERSIBLE
                </H1>
                <Box jc ac g={10}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                      stroke="#FF5252"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <P c="text-danger">
                    By claiming your unstaked Vaults you will not be able to
                    stake them in the future
                  </P>
                </Box>
                {isLoggedIn ? (
                  <ClaimButton
                    onClick={() => setHasConfirmedClaimAction(true)}
                    label="Claim"
                  />
                ) : (
                  <ConnectWalletButton />
                )}
              </Box>
            </Box>
          )}
        </div>
      </CheckoutModalOverlay>
    )
  }

  const transactionProcessingStatesLabel = {
    approve: 'Approving',
    staking: 'Staking',
    claiming: 'Claiming',
  }

  const isTransactionCompleted =
    tokensStakingStatus === 'success' ||
    vaultsStakingStatus === 'success' ||
    vaultsClaimStatus === 'success'

  return (
    <CheckoutModalOverlay isOpen={isOpen} dismiss={dismiss}>
      <div style={{ width: isMobile ? '340px' : '628px', maxWidth: '100%' }}>
        {status === 'idle' && (
          <Box dc g={32}>
            <PureBackButton onClick={onClose} />
            <H2 s="XL" fw="semibold" c="text-primary">
              Overview
            </H2>
            {isStake && (
              <Box dc g={16}>
                <P s="L" fw="regular" c="text-primary">
                  You are staking
                </P>
                <AmountBox dc padV={16} padH={16} g={12}>
                  <Box jsb ac>
                    <P s="M" fw="regular" c="text-secondary">
                      Amount
                    </P>
                    {target === 'vaults' && (
                      <P s="M" fw="regular" c="text-secondary">
                        Est. token amount
                      </P>
                    )}
                  </Box>
                  <Divider />
                  <Box jsb ac>
                    <Box ac g={8}>
                      <img
                        style={{ height: '38px' }}
                        src={iconSrc}
                        alt="icon"
                      />
                      <Box dc jc g={2}>
                        <P s="L" fw="semibold" c="text-primary">
                          {amount.toLocaleString()} {name}
                        </P>
                        <P s="S" fw="regular" c="text-secondary">
                          {months} months
                        </P>
                      </Box>
                    </Box>
                    <Box fu aw>
                      {target === 'vaults' && (
                        <Strong>{rawTotalTokens.toLocaleString()}</Strong>
                      )}
                    </Box>
                  </Box>
                </AmountBox>
              </Box>
            )}
            {isClaim && (
              <Box dc g={16}>
                <P s="L" fw="regular" c="text-primary">
                  You are claiming
                </P>
                <AmountBox dc padV={16} padH={16} g={12}>
                  <Box jsb ac>
                    <P s="M" fw="regular" c="text-secondary">
                      Amount
                    </P>
                    <P s="M" fw="regular" c="text-secondary">
                      Est. token amount
                    </P>
                  </Box>
                  <Divider />
                  <Box jsb ac>
                    <Box ac g={8}>
                      <img
                        style={{ height: '38px' }}
                        src={iconSrc}
                        alt="icon"
                      />
                      <Box ac g={2}>
                        <P s="L" fw="semibold" c="text-primary">
                          {amount.toLocaleString()} {name}
                        </P>
                      </Box>
                    </Box>
                    <Box fu aw>
                      <Strong>{rawTotalTokens.toLocaleString()}</Strong>
                    </Box>
                  </Box>
                </AmountBox>
              </Box>
            )}
            <Box dc g={32}>
              <H3 s="XL" fw="semibold" c="text-primary">
                What you'll receive
              </H3>
              <AmountBox
                dc
                padV={16}
                padH={16}
                g={12}
                highlight={isStake && !isClaim}
                highlightColor="brandPrimary"
              >
                <Box jsb ac>
                  {target === 'tokens' && (
                    <>
                      <P s="M" fw="regular" c="text-secondary">
                        ~ Current APY
                      </P>
                      <P s="M" fw="regular" c="text-secondary">
                        Est. amount
                      </P>
                    </>
                  )}
                  {target === 'vaults' && (
                    <>
                      <P s="M" fw="regular" c="text-secondary">
                        Bonus
                      </P>
                      <P s="M" fw="regular" c="text-secondary">
                        Est. token amount
                      </P>
                    </>
                  )}
                </Box>
                <Divider />
                <Box jsb ac>
                  {target === 'tokens' && (
                    <>
                      <Box ac g={8}>
                        <img
                          style={{ height: '48px' }}
                          src="/zap-coin.svg"
                          alt="zap coin icon"
                        />
                        <H4
                          s="XL"
                          fw="semibold"
                          c={
                            totalApy === 0 ? 'text-error' : 'text-brandPrimary'
                          }
                        >
                          {totalApy}%
                        </H4>
                      </Box>
                      <Box ac g={8} fu aw>
                        <Box dc jc g={2}>
                          <H4 s="XL" fw="semibold" c="text-brandPrimary" uc>
                            {estTokenValueTotal.toLocaleString()}
                          </H4>
                        </Box>
                      </Box>
                    </>
                  )}
                  {target === 'vaults' && (
                    <>
                      <Box ac g={8}>
                        <img src="/zap-coin.svg" alt="zap coin icon" />
                        <Box dc jc g={2}>
                          <H4
                            s="XL"
                            fw="semibold"
                            c={
                              totalApy === 0
                                ? 'text-error'
                                : 'text-brandPrimary'
                            }
                            uc
                          >
                            {totalApy.toFixed(2)}% Apy
                          </H4>
                          <P
                            s="S"
                            fw="regular"
                            c={
                              multiplier === 0
                                ? 'text-error'
                                : 'text-brandPrimary'
                            }
                          >
                            {multiplier}x multiplier
                          </P>
                        </Box>
                      </Box>
                      <Box dc fu aw>
                        <H5
                          s="XL"
                          fw="semibold"
                          c={
                            totalApy === 0 || multiplier === 0
                              ? 'text-primary'
                              : 'text-brandPrimary'
                          }
                        >
                          {estTokenValueTotal.toLocaleString()}
                        </H5>
                      </Box>
                    </>
                  )}
                </Box>

                {isStake && (
                  <Box g={4} ac padT={4}>
                    <AlertCircle color="white" size={16} />
                    <P s="M" fw="regular" c="text-secondary">
                      Please note actual received benefit may vary with APY
                    </P>
                  </Box>
                )}
              </AmountBox>
              {isClaim && (
                <OutcomeAlert
                  label={`You're missing out on ${maxEstimatedExtraTokens.toLocaleString()} extra tokens!`}
                  type="error"
                  onClick={onClose}
                />
              )}
            </Box>
            {isStake && (
              <Box dc g={10}>
                <CheckboxItem
                  ac
                  onClick={() => setAutoRelock(!autoRelock)}
                  isSelected={autoRelock}
                >
                  <CustomCheckbox isSelected={autoRelock} />
                  <Strong s="M" fw="semibold" ls="-0.28px" c="text-primary">
                    Auto re-lock
                  </Strong>
                </CheckboxItem>
                <P s="S" fw="regular" c="text-secondary">
                  Auto-relocking will automatically restake your assets for 12
                  months. If you select this you will have 48 hours at the end
                  of the lock period to unstake penalty-free.
                </P>
              </Box>
            )}
            <Box>
              {isLoggedIn ? (
                <ThemedButton $styleType="primary" onClick={onCheckout}>
                  Confirm
                </ThemedButton>
              ) : (
                <ConnectWalletButton />
              )}
            </Box>
          </Box>
        )}
        {status === 'transactions' && (
          <Box dc g={32} c>
            <ProcessingImage
              src={`/img/loaders/staking/${target}/${
                isTransactionCompleted ? 'done' : 'processing'
              }.png`}
            />
            <Box dc g={16} c>
              {isTransactionCompleted && (
                <H2 s="2XL" fw="bolder" c="text-primary" uc>
                  {'Complete!'}
                </H2>
              )}
              <Box dc g={12} c>
                {[
                  tokensStakingStatus,
                  vaultsStakingStatus,
                  vaultsClaimStatus,
                ].map((status, index) => {
                  if (status === 'idle' || status === 'error') return null
                  return (
                    <Box jc g="var(--units-8, 8px)">
                      {status !== 'success' ? <Spinner /> : <CheckBox filled />}
                      <Strong ls="-0.28px">
                        {transactionProcessingStatesLabel[status]}
                      </Strong>
                    </Box>
                  )
                })}
              </Box>
              {isTransactionCompleted && (
                <ThemedButton $styleType="primary" onClick={onComplete}>
                  View in your account
                </ThemedButton>
              )}
            </Box>
          </Box>
        )}
      </div>
    </CheckoutModalOverlay>
  )
}

export default StakingCheckout

export function CheckoutModalOverlay({
  children,
  isOpen,
  dismiss,
}: {
  children: ReactNode
  isOpen: boolean
  dismiss: () => void
}) {
  return (
    // https://floating-ui.com/docs/dialog#reusable-dialog-component
    // Check the above to understand if you need to extend it
    <Dialog dismissOnClickOutside={false} onOpenChange={dismiss} open={isOpen}>
      <DialogContent style={{ background: 'var(--surface-primary, #080808)' }}>
        {children}
        <Toaster />
      </DialogContent>
    </Dialog>
  )
}

const AmountBox = styled(Box)<{
  highlight?: boolean
  highlightColor?: 'error' | 'brandPrimary'
}>`
  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));

  ${({ highlight, highlightColor, theme }) =>
    highlight &&
    css`
      border: var(--units-1, 1px) solid
        ${theme.semantics.zap.text[highlightColor]};
    `}
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: var(--stroke-tertiary, #252525);
`

const CheckBox = ({ filled }: { filled?: boolean }) => {
  if (filled) {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0.5" y="0.5" width="23" height="23" rx="11.5" fill="#FFFF00" />
        <rect
          x="0.5"
          y="0.5"
          width="23"
          height="23"
          rx="11.5"
          stroke="#FFFF00"
        />
        <path
          d="M18.3637 7.22729L9.61375 15.9773L5.63647 12"
          stroke="#080808"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  return (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="1"
        y="0.5"
        width="23"
        height="23"
        rx="11.5"
        fill="white"
        fillOpacity="0.06"
      />
      <rect x="1" y="0.5" width="23" height="23" rx="11.5" stroke="#333333" />
    </svg>
  )
}

const Spinner = styled.div`
  display: flex;
  width: var(--units-24, 24px);
  height: var(--units-24, 24px);
  padding: 13px var(--units-12, 12px);
  justify-content: center;
  align-items: center;
  gap: var(--units-8, 8px);
  border-radius: var(--units-88, 88px);
  border: var(--units-1, 1px) solid var(--stroke-secondary, #333);
  background: var(--surface-minimal-primary, rgba(255, 255, 255, 0.06));
  position: relative;

  ::before {
    content: '';
    position: absolute;
    width: 80%;
    height: 80%;
    border-radius: 50%;
    border-top: 3px solid var(--icon-brand, #ff0);
    border-right: 3px solid transparent;
    border-left: 3px solid transparent;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const ProcessingImage = styled.img`
  width: 373px;
  z-index: -100;
  transition: transform 0.2s ease;
`
